import React, { useEffect, useState, useRef } from "react";
import { useAdvancedValidation } from "hooks/useAdvancedValidation";
import "./LocationTracking.css";
import MainLayout from "components/layout/main-layout/MainLayout";
import { titles } from "constants/titles";
import CustomButton from "components/widgets/custom-button/CustomButton";
import MessageSnackBar from "components/widgets/message-snackbar/MessageSnackBar";
import { IconTracking, IconMapPin } from "assets/SVGs/SVGs";
import {
  getAneltoTimeline,
  getClairvoyantTimeline,
  getEviewTimeLine,
  getFreeusTimeline,
  setDeviceDetailLoaderVisibility,
  getDeviceDetails,
  callSycsDevideDetails,
  setDeviceSyncLog,
  callDeviceSynLogs,
} from "redux/Devices/actions";
import { useDispatch, useSelector } from "react-redux";
import { convertInRequiredTimeZone } from "utility";
import { useHistory, useLocation } from "react-router-dom";
import ConfirmationModal from "components/layout/confirmation-modal/ConfirmationModal";
import { loader_gif, curv } from "assets/images/images";
import {
  eventLogs,
  getCalculatedLastRefreshTime,
} from "../../utility/deviceDetailUtils";
import FrequentRequestPopup from "components/layout/frequent-request-popup/FrequentRequestPopup";
import { useDeviceSyncStatus } from "hooks/useDeviceSyncStatus";
import { useDeviceManualRefresh } from "hooks/useDeviceManualRefresh";
import { useDeviceSocket } from "hooks/useDeviceSocket";
import { SYNC_STATUS } from "constants/syncStatus";
import moment from "moment";

const LocationTracking = () => {
  const [locationSignalPopup, setLocationSignalPopup] = useState(false);
  const history = useHistory();
  const location = useLocation();

  // Initialize validation hook
  const { snackbarState, dismissSnackbar } = useAdvancedValidation();
  const [deviceLocation, setDeviceLocation] = useState({
    latitude: location?.state?.item?.latitude
      ? parseFloat(location?.state?.item?.latitude, 10)
      : null,
    longitude: location?.state?.item?.longitude
      ? parseFloat(location?.state?.item?.longitude, 10)
      : null,
    latitudeDelta: 0.02,
    longitudeDelta: 0.02,
  });
  const [lastRefreshDetails, setLastRefreshDetails] = useState({
    time: "",
    city: "",
    state: "",
  });
  const [manualRefreshIntervalTime, setManualRefreshIntervalTime] = useState(0);
  const [frequentRequestPopup, setFrequentRequestPopup] = useState({
    data: null,
    isVisible: false,
  });
  const {
    selectedDeviceDetails,
    loading,
    freeusIntervals,
    aneltoIntervals,
    clairvoyantIntervals,
    deviceDetailLoading,
    eViewIntervalArray,
    manualRefreshLoading,
    deviceDetails,
    deviceSyncLogs,
    deviceSyncData,
  } = useSelector((store) => store?.devices);
  const userData = useSelector((store) => store?.auth?.userData);
  const { selectedMedicalUser } = useSelector(
    (store) => store?.medicalAlertUsers
  );
  const { currentTimeZone } = useSelector((store) => store?.common);
  const item = location?.state?.item;
  const isFromSignalHistory = location?.state?.isFromSignalHistory;
  const isButtonDisableFromParams = location?.state?.isButtonDisable;
  const dispatch = useDispatch();

  // Initialize refs for sync logs (matching RN app pattern)
  const deviceSyncLogsRef = useRef(deviceSyncLogs);
  const syncRef = useRef(deviceSyncData);
  const prevDisableStateRef = useRef(null);
  const prevIsSyncExpiredRef = useRef(false);

  // Define customSchemaForUI before hooks (matching RN app pattern)
  const customSchemaForUI = () => {
    const lastRequestedTime = getCalculatedLastRefreshTime(
      deviceDetails?.syncHistory?.syncRequestedOn,
      deviceDetails?.syncHistory?.syncCompletedOn
    );
    return {
      lastRefreshTime: lastRequestedTime,
      lastRequestedTime: deviceDetails?.syncHistory?.syncRequestedOn,
      syncCompletedOn: deviceDetails?.syncHistory?.syncCompletedOn,
      dataTimeStamp: deviceDetails?.deviceData?.dataTimestamp,
    };
  };

  // Use shared hooks - match RN app
  const { buttonText, isSyncing, isSyncExpired } = useDeviceSyncStatus(
    deviceDetails,
    titles?.update_location
  );

  const {
    disableManualRefresh: hookDisableManualRefresh,
    checkManualRefreshButtonAccess,
    hasCalculated,
  } = useDeviceManualRefresh(
    deviceDetails,
    customSchemaForUI,
    selectedDeviceDetails
  );

  // Use hook value if it's been calculated, otherwise fall back to param value
  const disableManualRefresh = hasCalculated
    ? hookDisableManualRefresh
    : (isButtonDisableFromParams ?? false);

  // Update refs when data changes (matching RN app pattern)
  useEffect(() => {
    deviceSyncLogsRef.current = deviceSyncLogs;
    syncRef.current = deviceSyncData?.syncId;
  }, [deviceSyncLogs, deviceSyncData]);

  // Auto-fetch device data when button becomes enabled after 3 minutes
  // This is a fallback mechanism if sync is still in-progress after 3 minutes
  useEffect(() => {
    // Check if button state changed from disabled (true) to enabled (false)
    if (
      prevDisableStateRef.current === true &&
      disableManualRefresh === false
    ) {
      // Button was just enabled after timer expired - fetch latest device data
      // Also check if sync is still in-progress (fallback mechanism)
      const isStillInProgress = deviceDetails?.syncHistory?.syncStatus === SYNC_STATUS.IN_PROGRESS;
      
      if (!isFromSignalHistory && selectedDeviceDetails?.user_device_id && isStillInProgress && !isSyncExpired) {
        // Add sync log before calling API
        setDeviceSyncLog(dispatch, {
          log: eventLogs?.fallback_trigger,
          loggedAt: moment()?.utc()?.toISOString(),
        });
        const payload = {
          dispatch,
          history,
          userType: userData?.isGuestUser ? "guestuser" : "accountowner",
          userDeviceId: selectedDeviceDetails?.user_device_id,
          isAutoTriggered: true,
        };
        getDeviceDetails(payload);
      }
    }
    // Update previous state
    prevDisableStateRef.current = disableManualRefresh;
  }, [
    disableManualRefresh,
    isFromSignalHistory,
    selectedDeviceDetails?.user_device_id,
    deviceDetails?.syncHistory?.syncStatus,
    isSyncExpired,
    dispatch,
    history,
    userData?.isGuestUser,
  ]);

  // Auto-fetch device data when sync expires at 3 minutes 10 seconds
  // This is a fallback mechanism if sync is still in-progress when it expires
  useEffect(() => {
    // Check if isSyncExpired changed from false to true
    if (
      prevIsSyncExpiredRef.current === false &&
      isSyncExpired === true
    ) {
      // Sync expired at 3 minutes 10 seconds - fetch latest device data
      // Only call API if sync is still in-progress (fallback mechanism)
      const isStillInProgress = deviceDetails?.syncHistory?.syncStatus === SYNC_STATUS.IN_PROGRESS;
      
      if (!isFromSignalHistory && selectedDeviceDetails?.user_device_id && isStillInProgress) {
        // Add sync log before calling API
        setDeviceSyncLog(dispatch, {
          log: eventLogs?.fallback_trigger,
          loggedAt: moment()?.utc()?.toISOString(),
        });
        const payload = {
          dispatch,
          history,
          userType: userData?.isGuestUser ? "guestuser" : "accountowner",
          userDeviceId: selectedDeviceDetails?.user_device_id,
          isAutoTriggered: true,
        };
        getDeviceDetails(payload);
      }
    }
    // Update previous state
    prevIsSyncExpiredRef.current = isSyncExpired;
  }, [
    isSyncExpired,
    isFromSignalHistory,
    selectedDeviceDetails?.user_device_id,
    deviceDetails?.syncHistory?.syncStatus,
    dispatch,
    history,
    userData?.isGuestUser,
  ]);

  useEffect(() => {
    setDeviceSyncLog(dispatch, {
      log: eventLogs?.enter_location_screen,
      loggedAt: moment()?.utc()?.toISOString(),
    });
    const param = {
      dispatch,
      history,
      userType: userData?.isGuestUser ? "guestuser" : "accountowner",
    };
    getAneltoTimeline(param);
    getFreeusTimeline(param);
    getClairvoyantTimeline(param);
    getEviewTimeLine(param);
    return () => {
      setDeviceDetailLoaderVisibility(dispatch, false);
      if (syncRef?.current) {
        callDeviceSynLogs({
          dispatch,
          history,
          userType: userData?.isGuestUser ? "guestuser" : "accountowner",
          payload: {
            syncId: syncRef?.current,
            syncLogs: [
              ...deviceSyncLogsRef?.current,
              {
                log: eventLogs?.left_screen,
                loggedAt: moment()?.utc()?.toISOString(),
              },
            ],
          },
        });
      }
    };
  }, []);

  // Match RN app: check manual refresh button access when deviceDetails changes
  useEffect(() => {
    try {
      // Always check manual refresh button access when deviceDetails changes
      // This ensures button state updates after sync completes, even if isButtonDisableFromParams was passed
      if (
        !isFromSignalHistory &&
        deviceDetails?.syncHistory?.syncStatus !== SYNC_STATUS.IN_PROGRESS &&
        selectedDeviceDetails
      ) {
        checkManualRefreshButtonAccess();
      }
      if (
        !isFromSignalHistory &&
        deviceDetails &&
        deviceDetails?.syncHistory?.syncStatus === SYNC_STATUS.IN_PROGRESS
      ) {
        setDeviceSyncLog(dispatch, {
          log: eventLogs?.sync_inprogress,
          loggedAt: moment()?.utc()?.toISOString(),
        });
      }
    } catch (e) {}
  }, [deviceDetails, selectedDeviceDetails]);

  // Use shared socket hook - match RN app
  useDeviceSocket((data) => {
    if (
      !isFromSignalHistory &&
      data?.userDeviceId === selectedDeviceDetails?.user_device_id
    ) {
      const payload = {
        dispatch,
        history,
        userType: userData?.isGuestUser ? "guestuser" : "accountowner",
        userDeviceId: selectedDeviceDetails?.user_device_id,
        isAutoTriggered: true,
      };
      getDeviceDetails(payload);
      callDeviceSynLogs({
        dispatch,
        history,
        userType: userData?.isGuestUser ? "guestuser" : "accountowner",
        payload: {
          syncId: syncRef?.current,
          syncLogs: [
            ...deviceSyncLogsRef?.current,
            {
              log: eventLogs?.data_updated,
              loggedAt: moment()?.utc()?.toISOString(),
            },
          ],
        },
      });
    }
  });

  const clearEviewInterval = (timeoutId) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };

  // Match RN app: update device location when deviceDetails changes
  useEffect(() => {
    if (!isFromSignalHistory && deviceDetails) {
      const locationData = deviceDetails?.deviceData?.locationData;
      // Check if latitude and longitude are available and valid
      const hasValidLatitude = locationData?.latitude && 
        locationData?.latitude !== 0 && 
        locationData?.latitude !== null && 
        !isNaN(parseFloat(locationData?.latitude));
      const hasValidLongitude = locationData?.longitude && 
        locationData?.longitude !== 0 && 
        locationData?.longitude !== null && 
        !isNaN(parseFloat(locationData?.longitude));
      
      if (hasValidLatitude && hasValidLongitude) {
        setDeviceLocation({
          ...deviceLocation,
          latitude: parseFloat(locationData?.latitude, 10),
          longitude: parseFloat(locationData?.longitude, 10),
        });
        setLastRefreshDetails({
          ...lastRefreshDetails,
          time: locationData?.locationTimestamp,
          city: locationData?.city ?? "",
          state: locationData?.state ?? "",
        });
      } else {
        // If lat/long are not available after API response, set to null to show "Map Not Available"
        setDeviceLocation({
          ...deviceLocation,
          latitude: null,
          longitude: null,
        });
      }
    }
  }, [selectedDeviceDetails, deviceDetails]);

  const getDeviceDetailManually = (isManual = false) => {
    // Match RN app: use callSycsDevideDetails (unified API for all device types)
    const params = {
      dispatch,
      userDeviceId: selectedDeviceDetails?.user_device_id,
      history,
      userType: userData?.isGuestUser ? "guestuser" : "accountowner",
      isManual: true,
    };
    callSycsDevideDetails(params);
  };

  const convertInLocalTimeZone = (date) => {
    return convertInRequiredTimeZone(
      date,
      selectedMedicalUser?.timezone,
      currentTimeZone
    );
  };

  const onManualRefreshClick = () => {
    if (manualRefreshLoading) {
      return;
    } else if (disableManualRefresh) {
      setFrequentRequestPopup({
        isVisible: true,
        lastRefreshTime: customSchemaForUI()?.lastRefreshTime,
      });
    } else {
      getDeviceDetailManually(true);
      setTimeout(() => {
        setLocationSignalPopup(true);
      }, 500);
    }
  };

  return (
    <MainLayout
      text={titles?.caregiver_tracking}
      isBackHide={false}
      isSemiHeader={true}
      white={true}
      primary={true}
      loading={loading}
      fullWidthScreen
    >
      {/* Snackbar - always sticky below header */}
      {snackbarState?.show && (
        <div className="snackbar_sticky">
          <MessageSnackBar
            message={snackbarState?.message}
            isVisible={true}
            type={snackbarState?.type}
            autoHideDuration={snackbarState?.autoHideDuration}
            onDismiss={dismissSnackbar}
          />
        </div>
      )}

      {deviceDetailLoading ? (
        <div className="gif_loader_container">
          <img height="50px" width="50px" src={loader_gif} alt="loader" />
        </div>
      ) : null}
      <div className="lt_content_container">
        <div className="lt_device_info_container">
          <h2 className="lt_device_title">
            {selectedDeviceDetails?.device_name}
          </h2>

          {!isFromSignalHistory ? (
            <CustomButton
              className="lt_update_button"
              text={buttonText}
              onClick={() => 
                !isSyncing ?onManualRefreshClick():null
              }
              isGrayButton={
                disableManualRefresh || manualRefreshLoading || isSyncing
              }
              disabled={isSyncing || manualRefreshLoading}
              isLoading={isSyncing || manualRefreshLoading}
              width="100%"
            />
          ) : null}

          <div className="lt_status_container">
            {isFromSignalHistory ? (
              <p className="lt_status_text">
                {`${titles?.device_status_information} ${convertInLocalTimeZone(
                  item?.date
                )}`}
              </p>
            ) : (
              <>
                {customSchemaForUI()?.lastRequestedTime && (
                  <p className="lt_status_text">
                    {titles?.last_refresh +
                      convertInLocalTimeZone(
                        customSchemaForUI()?.lastRequestedTime
                      )}
                  </p>
                )}
                {deviceDetails?.deviceData?.locationData?.locationTimestamp && (
                  <p className="lt_status_text">
                    {`${
                      titles?.device_status_information
                    } ${convertInLocalTimeZone(
                     deviceDetails?.deviceData?.locationData?.locationTimestamp
                    )}`}
                  </p>
                )}
              </>
            )}
            <p className="lt_status_text">
              {`${lastRefreshDetails?.city}${
                lastRefreshDetails?.state ? ", " : ""
              }${lastRefreshDetails?.state}`}
            </p>
          </div>
        </div>

        <div
          className={
            deviceLocation?.latitude !== null &&
            deviceLocation?.latitude !== undefined &&
            deviceLocation?.longitude !== null &&
            deviceLocation?.longitude !== undefined &&
            deviceLocation?.latitude !== 0 &&
            deviceLocation?.longitude !== 0 &&
            !isNaN(deviceLocation?.latitude) &&
            !isNaN(deviceLocation?.longitude)
              ? "lt_map_container"
              : "lt_map_container_no_data"
          }
        >
          {!(
            deviceLocation?.latitude !== null &&
            deviceLocation?.latitude !== undefined &&
            deviceLocation?.longitude !== null &&
            deviceLocation?.longitude !== undefined &&
            deviceLocation?.latitude !== 0 &&
            deviceLocation?.longitude !== 0 &&
            !isNaN(deviceLocation?.latitude) &&
            !isNaN(deviceLocation?.longitude)
          ) ? (
              <div className="lt_map_not_available_container">
                <IconMapPin width="32" height="32" />
                <p className="lt_map_not_available_title">
                  {titles?.map_not_available}
                </p>
                <p className="lt_map_not_available_subtitle">
                  {titles?.cell_coverage_required}
                </p>
              </div>
          ) : (
            <>
              <iframe
                src={`https://maps.google.com/maps?q=${deviceLocation?.latitude},${deviceLocation?.longitude}&z=15&output=embed`}
                width="100%"
                height="100%"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="lt_map"
                className="lt_map"
              ></iframe>
              <div className="lt_curved_section">
                <img src={curv} alt="Curve" className="lt_curved_image" />
              </div>
              <div
                className="lt_tracking_icon"
                onClick={() =>
                  window.open(
                    `https://www.google.com/maps?q=${deviceLocation?.latitude},${deviceLocation?.longitude}`,
                    "_blank"
                  )
                }
              >
                <IconTracking color="#fff" />
                <p className="lt_icon_text">{titles?.maps}</p>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmationModal
        show={locationSignalPopup}
        setShow={setLocationSignalPopup}
        svgIcon={<IconTracking />}
        btnText={titles?.OK}
        title={titles?.location_signal}
        description={titles?.location_signal_desc}
        onClick={() => setLocationSignalPopup(false)}
      />
      <FrequentRequestPopup
        show={frequentRequestPopup?.isVisible}
        setShow={setFrequentRequestPopup}
        lastRefreshTime={frequentRequestPopup?.lastRefreshTime}
        deviceType={selectedDeviceDetails?.device_type?.trim()}
      />
    </MainLayout>
  );
};

export default LocationTracking;
